import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trophy, Download, ChevronLeft, ChevronRight, CheckCircle2, Clock3, ExternalLink, Users, RefreshCcw, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import TaskDialog from "@/components/TaskDialog";
import { STATUS_META } from "@/constants/status";
import { ClientTag } from "@/components/ClientTag";

/** Weekly Report + Monthly Leaderboard.
 *  Amit reviews the previous week every Monday — who finished, what links they
 *  attached, and who's still open. Points nudge people to close out on time.
 */

const MEDALS = ["🥇", "🥈", "🥉"];

// -- Date helpers (Mon–Sun IST). Everything is date-string only (no TZ math). --
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDate = (iso) => {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}`;
};
const fmtSpan = (t) => {
  const s = t.start_date ? fmtDate(t.start_date) : "";
  const d = t.deadline ? fmtDate(t.deadline) : "";
  if (s && d && s !== d) return `${s} → ${d}`;
  return d || s || "";
};
const startOfWeek = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = Mon
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const monthOf = (iso) => iso.slice(0, 7);

const statusPill = (s) => {
  const map = {
    ...Object.fromEntries(Object.entries(STATUS_META).map(([k, v]) => [k, v.pill.replace("border border-", "border-")])),
  };
  return map[s] || map.not_started;
};

export default function WeeklyReports() {
  const { user } = useAuth();
  // Init range = previous week (last Mon–Sun)
  const now = new Date();
  const thisMonday = startOfWeek(now);
  const lastMonday = addDays(thisMonday, -7);
  const lastSunday = addDays(lastMonday, 6);
  const [dateFrom, setDateFrom] = useState(toIso(lastMonday));
  const [dateTo, setDateTo] = useState(toIso(lastSunday));
  const [team, setTeam] = useState("ALL");
  const [teams, setTeams] = useState([]);
  const [data, setData] = useState({ rows: [], cutoff: "" });
  const [leaderboard, setLeaderboard] = useState({ month: monthOf(toIso(lastSunday)), rows: [] });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  // ---- Inline Task-Edit dialog (open from any row on the weekly card) ----
  // Rows on this page are minimal; fetch the full task before opening the
  // dialog so every field (assignees, links, priority, hours) is populated.
  const [editTask, setEditTask] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const openTaskEditor = async (taskId) => {
    try {
      const { data: full } = await api.get(`/tasks/${taskId}`);
      setEditTask(full);
      setEditOpen(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't open task");
    }
  };

  const loadReport = async (from, to, teamFilter) => {
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (teamFilter && teamFilter !== "ALL") params.team = teamFilter;
      const { data: r } = await api.get("/reports/weekly", { params });
      setData(r || { rows: [], cutoff: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load weekly report");
    } finally {
      setLoading(false);
    }
  };

  const loadLeaderboard = async (month) => {
    try {
      const { data: r } = await api.get("/reports/leaderboard", { params: { month } });
      setLeaderboard(r || { rows: [], month });
    } catch (_) { /* silent — page still usable */ }
  };

  useEffect(() => {
    api.get("/teams").then(({ data: d }) => setTeams(d.teams || [])).catch(() => setTeams([]));
    api.get("/users").then(({ data: d }) => setUsers(d || [])).catch(() => setUsers([]));
    loadReport(dateFrom, dateTo, team);
    loadLeaderboard(monthOf(dateTo));
  }, []);

  const shiftWeek = (dir) => {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    const nf = addDays(from, 7 * dir);
    const nt = addDays(to, 7 * dir);
    const nfIso = toIso(nf);
    const ntIso = toIso(nt);
    setDateFrom(nfIso);
    setDateTo(ntIso);
    loadReport(nfIso, ntIso, team);
    if (monthOf(ntIso) !== leaderboard.month) loadLeaderboard(monthOf(ntIso));
  };

  const applyRange = () => {
    loadReport(dateFrom, dateTo, team);
    loadLeaderboard(monthOf(dateTo));
  };

  const applyTeam = (v) => {
    setTeam(v);
    loadReport(dateFrom, dateTo, v);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (team && team !== "ALL") params.set("team", team);
      const res = await api.get(`/reports/weekly-pdf?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `LIM-Weekly-${team === "ALL" ? "team" : team}-${dateFrom}-to-${dateTo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "PDF download failed");
    } finally {
      setDownloading(false);
    }
  };

  const rows = data.rows || [];
  const summary = useMemo(() => {
    let totalPts = 0, totalFull = 0, totalHours = 0, totalCompleted = 0;
    rows.forEach((r) => {
      totalPts += r.points_this_week || 0;
      totalFull += r.fully_completed_week ? 1 : 0;
      totalHours += r.hours || 0;
      totalCompleted += r.tasks_completed_in_range || 0;
    });
    return { totalPts, totalFull, totalHours: totalHours.toFixed(2), totalCompleted, headcount: rows.length };
  }, [rows]);

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="weekly-reports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[0.7rem] uppercase tracking-[0.3em] text-white/40 flex items-center gap-2">
            <Trophy size={12} /> Weekly Review
          </div>
          <h1 className="text-3xl sm:text-4xl font-display tracking-tight">Every Monday, at a glance</h1>
          <p className="text-sm text-white/50 mt-1">
            Auto-generated from tasks + timer. Cutoff to close last week is <span className="text-white/70">{data.cutoff || "—"}</span> (next Monday 23:59 IST).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { loadReport(dateFrom, dateTo, team); loadLeaderboard(monthOf(dateTo)); }} className="border-white/15" data-testid="weekly-refresh-btn">
            <RefreshCcw size={13} className="mr-1.5" /> Refresh
          </Button>
          <Button onClick={downloadPdf} disabled={downloading} className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-5" data-testid="weekly-pdf-btn">
            <Download size={13} className="mr-1.5" /> {downloading ? "Preparing…" : "Download PDF"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-white/10 bg-black/25 p-3 flex flex-col lg:flex-row lg:items-end gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => shiftWeek(-1)} data-testid="weekly-prev-week" className="h-9 w-9 p-0 text-white/60 hover:text-white">
            <ChevronLeft size={16} />
          </Button>
          <div>
            <div className="text-[0.7rem] uppercase tracking-widest text-white/40">From</div>
            <Input type="date" data-testid="weekly-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="bg-black/40 border-white/10 mt-1 h-9 [color-scheme:dark]" />
          </div>
          <div>
            <div className="text-[0.7rem] uppercase tracking-widest text-white/40">To</div>
            <Input type="date" data-testid="weekly-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="bg-black/40 border-white/10 mt-1 h-9 [color-scheme:dark]" />
          </div>
          <Button variant="ghost" onClick={() => shiftWeek(1)} data-testid="weekly-next-week" className="h-9 w-9 p-0 text-white/60 hover:text-white">
            <ChevronRight size={16} />
          </Button>
          <Button onClick={applyRange} className="bg-white/10 hover:bg-white/15 h-9" data-testid="weekly-apply-range">Apply</Button>
        </div>
        <div className="flex items-center gap-2 lg:ml-auto">
          <div className="text-[0.7rem] uppercase tracking-widest text-white/40 mr-1">Team</div>
          <Select value={team} onValueChange={applyTeam}>
            <SelectTrigger data-testid="weekly-team-filter" className="bg-black/40 border-white/10 h-9 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-surface border-white/10 text-white">
              <SelectItem value="ALL">All teams</SelectItem>
              {teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryPill label="People" value={summary.headcount} />
        <SummaryPill label="Full-week finishers" value={summary.totalFull} accent="text-emerald-300" />
        <SummaryPill label="Tasks completed" value={summary.totalCompleted} />
        <SummaryPill label="Hours logged" value={`${summary.totalHours} h`} />
        <SummaryPill label="Points awarded" value={summary.totalPts} accent="text-[#E11D2E]" />
      </div>

      {/* Two columns: report cards + leaderboard */}
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-6">
        {/* Report cards */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-white/50 text-sm">Loading weekly report…</div>
          ) : rows.length === 0 ? (
            <div className="text-white/50 text-sm border border-white/10 rounded-lg p-6">No members in this range.</div>
          ) : rows.map((r) => (
            <div key={r.user_id} data-testid={`weekly-row-${r.user_id}`} className={`rounded-lg border p-4 ${r.fully_completed_week ? "border-emerald-500/40 bg-emerald-500/[0.04]" : "border-white/10 bg-black/25"}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  {r.avatar_url ? (
                    <img src={r.avatar_url} alt="" className="h-10 w-10 rounded-full border border-white/10" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-white/10 border border-white/10 flex items-center justify-center font-display">{(r.name || "?")[0]}</div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-[0.7rem] text-white/40 font-mono uppercase tracking-widest">{r.team}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {r.fully_completed_week && (
                    <span className="inline-flex items-center gap-1 text-[0.7rem] uppercase tracking-widest px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
                      <CheckCircle2 size={10} /> Full week
                    </span>
                  )}
                  <span className="text-[0.7rem] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/15 bg-white/5 text-white/70">
                    {r.hours} h logged
                  </span>
                  <span className="text-[0.7rem] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[#E11D2E]/40 bg-[#E11D2E]/10 text-[#E11D2E] font-mono">
                    {r.points_this_week} pts
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                {/* Completed */}
                <div>
                  <div className="overline flex items-center gap-1 mb-1.5"><CheckCircle2 size={11} className="text-emerald-400" /> Completed ({r.completed_tasks.length})</div>
                  {r.completed_tasks.length === 0 ? (
                    <div className="text-xs text-white/30 italic">No completions this week.</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {r.completed_tasks.map((t) => (
                        <li key={t.id} className="text-[12px] text-white/80 flex items-start justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => openTaskEditor(t.id)}
                            data-testid={`weekly-edit-completed-${t.id}`}
                            className="truncate flex-1 text-left hover:text-white hover:underline decoration-white/30 underline-offset-2"
                            title="Click to edit this task"
                          >
                            <ClientTag client={t.client} /><span className="text-white/50">{t.project_name ? `${t.project_name} · ` : ""}</span>{t.title}
                          </button>
                          <span className="flex items-center gap-1 shrink-0">
                            {fmtSpan(t) && <span className="text-[0.7rem] px-1.5 py-0.5 rounded border border-white/10 text-white/50 font-mono" data-testid={`weekly-date-${t.id}`}>{fmtSpan(t)}</span>}
                            {t.nas_link && <a href={t.nas_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[0.7rem] px-1.5 py-0.5 rounded border border-white/15 text-white/60 hover:text-white hover:border-white/30 inline-flex items-center gap-0.5" data-testid={`weekly-nas-${t.id}`}>NAS <ExternalLink size={9} /></a>}
                            {t.final_video_link && <a href={t.final_video_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[0.7rem] px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-0.5" data-testid={`weekly-final-${t.id}`}>Final <ExternalLink size={9} /></a>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {/* Ongoing */}
                <div>
                  <div className="overline flex items-center gap-1 mb-1.5"><Clock3 size={11} className="text-amber-400" /> Still open ({r.ongoing_tasks.length})</div>
                  {r.ongoing_tasks.length === 0 ? (
                    <div className="text-xs text-emerald-300/70 italic">Nothing pending — clean sheet.</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {r.ongoing_tasks.map((t) => (
                        <li key={t.id} className="text-[12px] text-white/70 flex items-start justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => openTaskEditor(t.id)}
                            data-testid={`weekly-edit-ongoing-${t.id}`}
                            className="truncate flex-1 text-left hover:text-white hover:underline decoration-white/30 underline-offset-2"
                            title="Click to edit this task"
                          >
                            <ClientTag client={t.client} /><span className="text-white/40">{t.project_name ? `${t.project_name} · ` : ""}</span>{t.title}
                          </button>
                          <span className="flex items-center gap-1 shrink-0">
                            {fmtSpan(t) && <span className="text-[0.7rem] px-1.5 py-0.5 rounded border border-white/10 text-white/50 font-mono" data-testid={`weekly-date-${t.id}`}>{fmtSpan(t)}</span>}
                            <span className={`text-[0.65rem] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${statusPill(t.status)}`}>
                              {(t.status || "").replace("_", " ")}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Leaderboard */}
        <div className="rounded-lg border border-white/10 bg-black/25 p-4 h-fit sticky top-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[0.7rem] uppercase tracking-widest text-white/40 flex items-center gap-1.5"><Trophy size={11} /> Leaderboard</div>
              <div className="text-sm font-display">{leaderboard.month}</div>
            </div>
            <div className="text-[0.7rem] text-white/40 font-mono">
              <Users size={10} className="inline mr-1" />{leaderboard.rows?.length || 0}
            </div>
          </div>
          <div className="space-y-1.5" data-testid="weekly-leaderboard">
            {(leaderboard.rows || []).map((r) => (
              <div
                key={r.user_id}
                data-testid={`weekly-leader-${r.user_id}`}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-md ${r.rank <= 3 ? "bg-white/5 border border-white/10" : ""} ${r.user_id === user?.id ? "ring-1 ring-[#E11D2E]/40" : ""}`}
              >
                <span className="w-6 text-center text-sm">
                  {r.rank <= 3 ? MEDALS[r.rank - 1] : <span className="text-white/40 font-mono text-xs">#{r.rank}</span>}
                </span>
                <span className="flex-1 truncate text-sm">{r.name}</span>
                <span className="text-[0.7rem] text-white/40 font-mono truncate max-w-[80px]">{r.team}</span>
                <span className="font-mono text-sm text-[#E11D2E] w-12 text-right">{r.points}</span>
              </div>
            ))}
          </div>
          <div className="text-[0.7rem] text-white/40 mt-3 leading-relaxed border-t border-white/5 pt-2">
            Points = 1 pt per task closed + <span className="text-emerald-300">10 pt bonus</span> per fully-finished week. Cutoff is next Monday.
          </div>
        </div>
      </div>

      {/* Inline task editor — refresh the visible week + leaderboard on save
          so status flips (e.g. marking a stuck task complete) appear right away. */}
      <TaskDialog
        open={editOpen}
        onOpenChange={(o) => { setEditOpen(o); if (!o) setEditTask(null); }}
        users={users}
        existing={editTask}
        onSaved={() => {
          loadReport(dateFrom, dateTo, team);
          loadLeaderboard(monthOf(dateTo));
        }}
      />
    </div>
  );
}

function SummaryPill({ label, value, accent }) {
  return (
    <div className="rounded-md border border-white/10 bg-black/25 p-3">
      <div className="text-[0.7rem] uppercase tracking-widest text-white/40">{label}</div>
      <div className={`text-xl font-display mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}
